/**
 * ReportBlocksRenderer
 *
 * Shared component that renders a flat list of ReportBlocks.
 * Handles the two block formats present in insight reports:
 * - InsightCardBlock: layout-based cards with block_elements (rendered via InsightCardBlockRenderer)
 * - FreeFlowBlock: plain UIBlock rendered directly via dispatchBlock
 *
 * Used by InsightCardPayloadRenderer, InsightCardMultiPageRenderer,
 * and ReportPreviewSlot — single source of truth for report block rendering.
 * Callers are responsible for any outer layout wrapper (e.g. <Container>).
 */

import React from 'react';
import type { ReportBlock, UIActionEvent } from '@/types/ui-blocks';
import { InsightCardBlockRenderer } from './insight-card-block-renderer';
import { dispatchBlock } from './block-renderer-utils';
import { isInsightCardBlock } from './slots/shared';

export interface ReportBlocksRendererProps {
  /** The list of report blocks to render */
  blocks: ReportBlock[];
  /** Callback fired when user interacts with UI components */
  onUIAction?: (event: UIActionEvent) => void;
  /** When true, interactive elements are disabled */
  readOnly?: boolean;
}

export const ReportBlocksRenderer: React.FC<ReportBlocksRendererProps> = ({
  blocks,
  onUIAction = () => {},
  readOnly,
}) => {
  return (
    <div className="flex flex-col gap-4">
      {blocks.map((block, index) => {
        if (isInsightCardBlock(block)) {
          return (
            <InsightCardBlockRenderer
              key={block.id}
              block={block}
              onUIAction={onUIAction}
            />
          );
        }

        return (
          <div key={block.id || `free-flow-${index}`}>
            {dispatchBlock({
              block,
              onUIAction,
              readOnly,
            })}
          </div>
        );
      })}
    </div>
  );
};

export default ReportBlocksRenderer;
